import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { useEffect } from "react";
import { LocationDetails } from "@/components/location-details";
import { PageHeader } from "@/components/page-header";
import { PageLoading } from "@/components/page-loading";
import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { locationQueryOptions, queryClient } from "@/lib/data";
import { retryIfCancelled } from "@/lib/query-client";
import { setSalesZoneReferral } from "@/lib/referral";
import { isSignedIn } from "@/lib/session";

export const Route = createFileRoute("/locations/$id")({
  component: LocationPage,
  loader: ({ params }) => {
    const options = locationQueryOptions(params.id);
    return retryIfCancelled(options.queryKey, () =>
      queryClient.ensureQueryData(options),
    );
  },
});

function LocationPage() {
  const { id } = Route.useParams();
  const { data: location, isLoading } = useQuery(locationQueryOptions(id));

  useEffect(() => {
    if (!isSignedIn() && location?.slug) {
      setSalesZoneReferral(location.slug);
    }
  }, [location?.slug]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!location) {
    return (
      <PageShell>
        <PageHeader title="Location not found" backTo={null} />
        <p className="text-sm text-muted-foreground">
          We couldn't find that location.
        </p>
      </PageShell>
    );
  }

  const authed = isSignedIn();

  return (
    <PageShell hasStickyFooter>
      <PageHeader
        title={location.name}
        description={location.physical_address ?? undefined}
        backTo={authed ? "/steps" : null}
      />

      <LocationDetails location={location} />

      {location.phone_number && (
        <Button
          asChild
          variant="outline"
          size="lg"
          className="mt-4 w-full"
          data-icon="inline-start"
        >
          <a href={`tel:${location.phone_number}`}>
            <Phone className="size-4" />
            Call {location.phone_number}
          </a>
        </Button>
      )}

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto w-full max-w-xl">
          <Button asChild size="lg" className="w-full">
            <Link to={authed ? "/steps" : "/"}>
              {authed ? "Return to your steps" : "Get started"}
            </Link>
          </Button>
        </div>
      </div>
    </PageShell>
  );
}
